import { useMemo, useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import {
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    getSortedRowModel,
    getPaginationRowModel,
    flexRender,
    type ColumnDef,
    type SortingState,
} from '@tanstack/react-table';
import { Button, EmptyState, ErrorState } from '@fiado/react-kit';
import { resolveErrorMessage } from '../../api/errorMapping';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { ContentLoading } from '../shared/ContentLoading';
import { DataTableToolbar } from '../shared/DataTableToolbar';
import { DataTablePagination } from '../shared/DataTablePagination';
import { SortableHeader } from '../shared/SortableHeader';
import { globalFilterFn } from '../../lib/dataTable';
import { formatDateTimeMx } from '../../lib/dateHelpers';
import { SELECT_CLASS } from '../../lib/selectClass';
import {
    AUDIT_ACTION_OPTIONS,
    auditActionLabel,
    auditRowActionLabel,
} from '../../lib/auditActionLabels';
import { Label } from '../ui/label';
import type { AuditScopeDescriptor } from './scopeDescriptors';
import type { AuditLogEntryLocal, AuditQueryResponseLocal } from '../../api/types/TenantLocal';

/**
 * Fila CRUDA del audit log tal cual viene del server (item del query). Es lo que reciben
 * `resolveActor`/`resolveTarget` para producir las etiquetas legibles (cada scope mapea sus propias
 * entidades). El motor enriquece internamente a `EnrichedAuditRow` para alimentar search/orden client.
 */
export type AuditRow = AuditLogEntryLocal;

// Fila enriquecida interna: etiquetas legibles ya resueltas, para que el global search opere sobre el
// texto visible y el orden/paginación sobre el modelo client.
interface EnrichedAuditRow {
    key: string;
    timestampIso: string;
    timestampLabel: string;
    actorLabel: string;
    action: string;
    targetLabel: string;
}

export interface ScopedAuditTableProps {
    descriptor: AuditScopeDescriptor;
    /**
     * scopeRef explícito que tiene prioridad sobre `descriptor.useScopeRef()` (mismo patrón que
     * ScopedUsersTable). Necesario para el flujo retailer: el call-site resuelve el retailer "en
     * contexto" con `useActiveRetailerId`.
     */
    scopeRef?: string;
    /** Nombre legible del actor de la fila (resuelto por el call-site con sus mapas). */
    resolveActor: (row: AuditRow) => string;
    /** Nombre legible del target de la fila (resuelto por el call-site con sus mapas). */
    resolveTarget: (row: AuditRow) => string;
    /** Usuarios con nombre para filtrar (valor = cognitoSub); sin opciones no hay filtro de usuario. */
    actorOptions?: AuditActorOption[];
}

export interface AuditActorOption {
    value: string;
    label: string;
}

/**
 * Tabla de auditoría unificada por scope (TENANT / RETAILER). Reemplaza AuditTable + RetailerAuditTable.
 * Todo el comportamiento (filtros action/actor server-side, search client, orden, paginación, "cargar
 * más") es idéntico; lo único parametrizado: API/scopeRef + queryKey (vía descriptor) y la resolución
 * de nombres de actor/target (vía props, porque las entidades a mapear varían por scope).
 *
 * No lleva <Gate>: son vistas internas y el backend enforza el scopeRef (igual que los componentes que
 * reemplaza).
 */
export function ScopedAuditTable({
    descriptor,
    scopeRef: scopeRefProp,
    resolveActor,
    resolveTarget,
    actorOptions,
}: ScopedAuditTableProps) {
    const descriptorScopeRef = descriptor.useScopeRef();
    const scopeRef = scopeRefProp ?? descriptorScopeRef;
    const [action, setAction] = useState('');
    const [actor, setActor] = useState('');
    // Search global client-side (sobre usuario/action/target ya resueltos) + orden client.
    const [globalFilter, setGlobalFilter] = useState('');
    const [sorting, setSorting] = useState<SortingState>([{ id: 'timestampIso', desc: true }]);
    const sortedActorOptions = useMemo(
        () => [...(actorOptions ?? [])].sort((a, b) => a.label.localeCompare(b.label)),
        [actorOptions],
    );

    const { data, fetchNextPage, hasNextPage, isLoading, isError, error, refetch } =
        useInfiniteQuery({
            queryKey: [...descriptor.queryKeyBase(scopeRef ?? ''), 'audit', { action, actor }],
            queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
                descriptor.api.queryAudit(scopeRef ?? '', {
                    cursor: pageParam,
                    pageSize: 25,
                    action: action || undefined,
                    actor: actor || undefined,
                }) as Promise<AuditQueryResponseLocal>,
            initialPageParam: undefined as string | undefined,
            getNextPageParam: (last) => last.lastEvaluatedKey,
            enabled: !!scopeRef,
        });

    // Filas enriquecidas: resolvemos actor/target/timestamp una vez (los resolvers dependen de los mapas
    // del call-site). Referencia estable (footgun de useReactTable: data nueva por render → loop).
    const rows = useMemo<EnrichedAuditRow[]>(() => {
        const items = data?.pages.flatMap((p) => p.items) ?? [];
        return items.map((r, i) => ({
            key: r.auditId || `audit-${String(i)}`,
            timestampIso: r.timestamp,
            timestampLabel: r.timestamp ? formatDateTimeMx(r.timestamp) : '—',
            actorLabel: resolveActor(r),
            action: r.action ? auditRowActionLabel(r.action, r.after) : '—',
            targetLabel: resolveTarget(r),
        }));
        // Los resolvers cierran sobre los mapas del call-site; éste los memoiza para estabilizar `rows`.
    }, [data, resolveActor, resolveTarget]);

    const columns = useMemo<ColumnDef<EnrichedAuditRow>[]>(
        () => [
            {
                // Ordena por el ISO (cronológico); la celda muestra la fecha formateada.
                accessorKey: 'timestampIso',
                header: ({ column }) => <SortableHeader column={column} title="Fecha" />,
                sortingFn: (a, b) => a.original.timestampIso.localeCompare(b.original.timestampIso),
                enableGlobalFilter: false,
                cell: ({ row }) => row.original.timestampLabel,
            },
            {
                accessorKey: 'actorLabel',
                header: ({ column }) => <SortableHeader column={column} title="Usuario" />,
            },
            {
                accessorKey: 'action',
                header: ({ column }) => <SortableHeader column={column} title="Acción" />,
            },
            {
                accessorKey: 'targetLabel',
                header: ({ column }) => <SortableHeader column={column} title="Recurso" />,
            },
        ],
        [],
    );

    const table = useReactTable({
        data: rows,
        columns,
        state: { globalFilter, sorting },
        onGlobalFilterChange: setGlobalFilter,
        onSortingChange: setSorting,
        globalFilterFn,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        initialState: { pagination: { pageSize: 25 } },
    });

    // Spinner del área de contenido mientras la primera página del audit log carga. `isLoading`
    // (no `isPending`) porque la query es condicional (`enabled: !!scopeRef`).
    if (isLoading) return <ContentLoading />;
    if (isError) {
        return (
            <ErrorState
                title="No pudimos cargar la auditoría"
                message={resolveErrorMessage(error)}
                retryLabel="Reintentar"
                onRetry={() => {
                    void refetch();
                }}
            />
        );
    }

    return (
        <div className="space-y-4">
            {/* Filtros de acción y usuario: van al server (queryKey); el search de la toolbar es client. */}
            <div className="grid sm:grid-cols-2 gap-3">
                <div className="space-y-2">
                    <Label htmlFor="f-action">Acción</Label>
                    <select
                        id="f-action"
                        className={SELECT_CLASS}
                        value={action}
                        onChange={(e) => {
                            setAction(e.target.value);
                        }}
                    >
                        <option value="">Todas</option>
                        {AUDIT_ACTION_OPTIONS.map((a) => (
                            <option key={a} value={a}>
                                {auditActionLabel(a)}
                            </option>
                        ))}
                    </select>
                </div>
                {sortedActorOptions.length > 0 && (
                    <div className="space-y-2">
                        <Label htmlFor="f-actor">Usuario</Label>
                        <select
                            id="f-actor"
                            className={SELECT_CLASS}
                            value={actor}
                            onChange={(e) => {
                                setActor(e.target.value);
                            }}
                        >
                            <option value="">Todos</option>
                            {sortedActorOptions.map((o) => (
                                <option key={o.value} value={o.value}>
                                    {o.label}
                                </option>
                            ))}
                        </select>
                    </div>
                )}
            </div>

            <DataTableToolbar
                value={globalFilter}
                onChange={setGlobalFilter}
                placeholder="Buscar en usuario, acción o recurso…"
            />

            {rows.length === 0 ? (
                action || actor ? (
                    <EmptyState
                        title="No hay movimientos con estos filtros"
                        description="Cambia la acción o el usuario para ver otros movimientos."
                    />
                ) : (
                    <EmptyState
                        title="Todavía no hay movimientos registrados"
                        description="Aquí verás cada cambio que se haga en este alcance."
                    />
                )
            ) : (
                <>
                    <Table>
                        <TableHeader>
                            {table.getHeaderGroups().map((hg) => (
                                <TableRow key={hg.id}>
                                    {hg.headers.map((h) => (
                                        <TableHead key={h.id} className="text-center">
                                            {flexRender(h.column.columnDef.header, h.getContext())}
                                        </TableHead>
                                    ))}
                                </TableRow>
                            ))}
                        </TableHeader>
                        <TableBody>
                            {table.getRowModel().rows.map((r) => (
                                <TableRow key={r.id}>
                                    {r.getVisibleCells().map((c) => (
                                        <TableCell key={c.id} className="text-center align-middle">
                                            {flexRender(c.column.columnDef.cell, c.getContext())}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>

                    <DataTablePagination table={table} />
                </>
            )}

            {/* "Cargar más" trae MÁS filas del server (cursor); la paginación numérica es sobre lo
                ya cargado. */}
            {hasNextPage && (
                <Button
                    variant="outline"
                    onClick={() => {
                        void fetchNextPage();
                    }}
                >
                    Cargar más
                </Button>
            )}
        </div>
    );
}
